import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Server, Sparkles, Mail, Phone, Linkedin, FileText } from "lucide-react";
import { ECOSYSTEMS } from "@/data/hannonexus";
import { useHannoContent } from "@/data/hanno-content";
import { HannoMark } from "@/components/hanno/HannoMark";
import { FlagCaboVerde, FlagSaoTome } from "@/components/hanno/Flags";

const NEON = "#0A9B5F";
const BSGOTECH_GREEN = "#0A9B5F";

export const Footer = () => {
  const c = useHannoContent();
  const navigate = useNavigate();

  // Disguised entries — admin (©) and finance (€)
  const adminTimer = React.useRef(null);
  const adminClicks = React.useRef({ count: 0, first: 0 });
  const adminTriggered = React.useRef(false);
  const finTimer = React.useRef(null);
  const finClicks = React.useRef({ count: 0, first: 0 });
  const finTriggered = React.useRef(false);

  const openAdmin = React.useCallback(() => {
    adminTriggered.current = true;
    navigate("/admin");
  }, [navigate]);
  const openFinance = React.useCallback(() => {
    finTriggered.current = true;
    navigate("/finance");
  }, [navigate]);

  const makeHandlers = (timerRef, clicksRef, triggeredRef, open) => ({
    onPointerDown: (e) => {
      e.preventDefault();
      triggeredRef.current = false;
      timerRef.current = setTimeout(() => open(), 600);
    },
    onPointerUp: () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (triggeredRef.current) return;
      const now = Date.now();
      if (now - clicksRef.current.first > 600) clicksRef.current = { count: 0, first: now };
      clicksRef.current.count += 1;
      if (clicksRef.current.first === 0) clicksRef.current.first = now;
      if (clicksRef.current.count >= 3) {
        clicksRef.current = { count: 0, first: 0 };
        open();
      }
    },
    onPointerLeave: () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
  });

  const adminHandlers = makeHandlers(adminTimer, adminClicks, adminTriggered, openAdmin);
  const financeHandlers = makeHandlers(finTimer, finClicks, finTriggered, openFinance);

  React.useEffect(() => () => {
    if (adminTimer.current) clearTimeout(adminTimer.current);
    if (finTimer.current) clearTimeout(finTimer.current);
  }, []);

  return (
    <footer data-testid="footer-section" className="relative border-t border-white/5 bg-hx-bg overflow-hidden">
      <div className="absolute inset-0 hx-grid-bg opacity-[0.14] pointer-events-none" />

      {/* Top brand strip */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-24 pb-14">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12 lg:gap-16 items-start">
          {/* Brand + tagline */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <HannoMark size={48} className="shrink-0" />
              <div className="leading-tight">
                <div className="font-display font-bold tracking-tight text-xl">HannoNexus</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.24em] text-hx-muted">{c.brand.tagline}</div>
              </div>
            </div>
            <h3 className="font-display font-semibold text-2xl lg:text-3xl text-hx-text leading-tight tracking-tight max-w-lg">
              {c.footer.tagline_l1} <br />
              <span className="text-hx-neon">{c.footer.tagline_l2}</span>
            </h3>
          </div>

          {/* Contact */}
          <div className="lg:pt-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-hx-muted font-semibold mb-4">{c.footer.contact}</div>
            <div className="flex flex-col gap-2.5">
              <a
                href="mailto:admin@bsgotech.com"
                data-testid="footer-email"
                className="group inline-flex items-center gap-2.5 text-hx-text/90 hover:text-hx-neon transition-colors"
              >
                <Mail size={13} strokeWidth={1.8} className="text-hx-neon" />
                <span className="text-sm">admin@bsgotech.com</span>
              </a>
              <a
                href="https://wa.me/4917684450554"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-phone"
                className="group inline-flex items-center gap-2.5 text-hx-text/90 hover:text-hx-neon transition-colors font-mono"
              >
                <Phone size={13} strokeWidth={1.8} className="text-hx-neon" />
                <span className="text-sm">+49 176 84450554</span>
              </a>
              <a
                href="https://www.linkedin.com/company/bsgotech/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="group inline-flex items-center gap-2.5 text-hx-text/90 hover:text-hx-neon transition-colors"
              >
                <Linkedin size={13} strokeWidth={1.8} className="text-hx-neon" />
                <span className="text-sm">LinkedIn</span>
              </a>
              <Link
                to="/press-kit"
                data-testid="footer-press-kit"
                className="group inline-flex items-center gap-2.5 text-hx-text/90 hover:text-hx-neon transition-colors"
              >
                <FileText size={13} strokeWidth={1.8} className="text-hx-neon" />
                <span className="text-sm">Press & Investor Kit</span>
              </Link>
              <Link
                to="/blueprint"
                data-testid="footer-blueprint"
                className="group inline-flex items-center gap-2.5 text-hx-text/90 hover:text-hx-neon transition-colors"
              >
                <FileText size={13} strokeWidth={1.8} className="text-hx-neon" />
                <span className="text-sm">The Living Blueprint</span>
              </Link>
              <Link
                to="/blueprint-automotive"
                data-testid="footer-blueprint-automotive"
                className="group inline-flex items-center gap-2.5 text-hx-text/90 hover:text-hx-neon transition-colors"
              >
                <FileText size={13} strokeWidth={1.8} className="text-hx-neon" />
                <span className="text-sm">Mobility Blueprint</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Corporate structure grid */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-12 border-t border-white/[0.10]">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">

          {/* Operator — BSGOTECH */}
          <div data-testid="footer-operator">
            <div className="text-[10px] uppercase tracking-[0.3em] text-hx-muted font-semibold mb-4">{c.footer.operator}</div>
            <motion.div
              animate={{ boxShadow: [`0 0 14px ${BSGOTECH_GREEN}33`, `0 0 24px ${BSGOTECH_GREEN}66`, `0 0 14px ${BSGOTECH_GREEN}33`] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md"
              style={{
                background: `linear-gradient(135deg, ${BSGOTECH_GREEN}1a 0%, transparent 70%)`,
                border: `1px solid ${BSGOTECH_GREEN}55`,
              }}
            >
              <Server size={13} strokeWidth={2} style={{ color: BSGOTECH_GREEN }} />
              <span
                data-testid="footer-bsgotech-wordmark"
                className="font-display font-bold tracking-tight text-base"
                style={{ color: BSGOTECH_GREEN, textShadow: `0 0 12px ${BSGOTECH_GREEN}66`, letterSpacing: "0.04em" }}
              >
                BSGOTECH
              </span>
            </motion.div>
            <p className="mt-3 text-[11.5px] text-hx-muted leading-relaxed">
              {c.footer.operated_by} <span className="font-medium" style={{ color: BSGOTECH_GREEN }}>{c.footer.operator_name}</span>
            </p>
          </div>

          {/* HX Ecosystems index */}
          <div data-testid="footer-ecosystems">
            <div className="text-[10px] uppercase tracking-[0.3em] text-hx-muted font-semibold mb-4">{c.footer.ecosystems}</div>
            <ul className="flex flex-col gap-1.5">
              {ECOSYSTEMS.map((e) => {
                const et = c.eco[e.slug];
                return (
                <li key={e.slug}>
                  <Link to={`/${e.slug}`} className="flex items-center gap-2 text-[12.5px] text-hx-text/90 hover:text-hx-neon transition-colors">
                    <span className="w-1 h-1 rounded-full" style={{ background: NEON }} />
                    {et.short}
                  </Link>
                </li>
                );
              })}
            </ul>
          </div>

          {/* Strategic markets */}
          <div data-testid="footer-markets">
            <div className="text-[10px] uppercase tracking-[0.3em] text-hx-muted font-semibold mb-4">{c.footer.markets}</div>
            <ul className="flex flex-col gap-1.5">
              {[
                { slug: "cabo-verde", name: "Cabo Verde" },
                { slug: "sao-tome",   name: "São Tomé e Príncipe" },
                { emoji: "🇦🇴", name: "Angola" },
                { emoji: "🇲🇿", name: "Moçambique" },
                { emoji: "🇬🇼", name: "Guiné-Bissau" },
                { emoji: "🇹🇱", name: "Timor-Leste" },
              ].map((m) => (
                <li key={m.name} className="flex items-center gap-2 text-[12.5px] text-hx-text/90">
                  {m.slug === "cabo-verde" && <FlagCaboVerde className="w-4 h-3 rounded-[1px]" />}
                  {m.slug === "sao-tome" && <FlagSaoTome className="w-4 h-3 rounded-[1px]" />}
                  {m.emoji && <span className="text-sm">{m.emoji}</span>}
                  {m.name}
                </li>
              ))}
            </ul>
            <div className="mt-4">
              <motion.div
                animate={{ boxShadow: [`0 0 10px ${NEON}22`, `0 0 20px ${NEON}44`, `0 0 10px ${NEON}22`] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md"
                style={{ background: `${NEON}0a`, border: `1px solid ${NEON}44` }}
              >
                <Sparkles size={11} strokeWidth={2.2} style={{ color: NEON }} />
                <span className="text-[10px] uppercase tracking-[0.22em] font-mono font-semibold" style={{ color: NEON }}>
                  {c.footer.cplp_badge}
                </span>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom strip */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-6 border-t border-white/[0.10]">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[10px] uppercase tracking-[0.24em] text-hx-muted font-mono">
          <span>
            <span
              role="presentation"
              data-testid="footer-admin-handle"
              {...adminHandlers}
              className="select-none inline-block px-1"
              style={{ userSelect: "none", WebkitUserSelect: "none", touchAction: "none" }}
            >
              ©
            </span>{" "}
            {new Date().getFullYear()} {c.footer.copyright}
          </span>
          <span className="flex items-center gap-1.5">
            {c.footer.architected}
            <span
              role="presentation"
              data-testid="footer-finance-handle"
              {...financeHandlers}
              className="select-none inline-block px-1 opacity-60 hover:opacity-100 transition-opacity"
              style={{ userSelect: "none", WebkitUserSelect: "none", touchAction: "none" }}
              title=""
            >
              €
            </span>
          </span>
        </div>
      </div>
    </footer>
  );
};
